import React, {FC, useEffect, useRef, useState, useMemo, useLayoutEffect} from 'react';
import userImage from 'assets/images/image1.png';
import closeImage from 'assets/svg/close.svg';
import ChatHeader from 'components/chatContent/chatHeader/ChatHeader';
import ChatFooter from 'components/chatContent/chatFooter/ChatFooter';
import Styles from 'components/chatContent/ChatContent.module.scss';
import MessageBox from './messageBox/MessageBox';
import { updateParticipantsInRoom, UpdateHostInRoom, sendJoinRequest } from 'store/services/firebase-broadcast';
import { getCurrentDate, randomString, getMessageTime } from 'helpers/utils';
import joinImage from 'assets/svg/join button.svg';
import socialPlayFavIcon from 'assets/images/socialPlayFavIcon.png';
import CustomModal from 'components/customModal/CustomModal';
import { useHistory } from 'react-router';
import Button from 'components/button/Button';
import Loader from 'components/loader/loader';
import Toast from 'components/toast/Tost';
import VideoPlayer from 'components/videoPlayer/VideoPlayer';

interface ChatContentProps {
  viewReplies: Function;
  replyItems: Function;
  chatInfo: Function;
  chats: any;
  selected: any;
  actions: boolean;
  activateRoom?: any;
  // Pagination props for dynamic loading
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadOlderMessages?: () => Promise<void>;
  // Initialization state for scroll control
  isInitialized?: boolean;
}
const ChatContent: FC<ChatContentProps> = (props: ChatContentProps) => {
  const { viewReplies, replyItems, chatInfo, chats, selected, actions, activateRoom, hasMore, isLoadingMore, loadOlderMessages, isInitialized } = props;
  const [typing, setTyping] = useState(false);
  const [isJoined, setIsJoined] = useState<boolean>(false);
  const [images, setImages] = useState<any>([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [chatContent] = useState(chats);
  const latestMsgId: any = useRef(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true); // Track if this is the very first load
  const scrollPositionRef = useRef(0); // Track scroll position before loading older messages
  const previousScrollHeightRef = useRef(0); // Track previous scroll height
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [modalToggle, setModalToggle] = useState(false);
  const [toast, setToast] = useState(false);
  const [toastValues, setToastValues] = useState({message: '', type: ''});
  const history = useHistory();
  useEffect(() => {}, [images]);
  
  // ADD: Scroll event listener for dynamic loading
  useEffect(() => {
    const handleScroll = () => {      
      if (!hasMore || isLoadingMore || !loadOlderMessages) return;
      
      const scrollTop = chatContainerRef.current?.scrollTop || 0;
      // Load more messages when user scrolls to top (within 100px)
      if (scrollTop < 50) {
        console.log("load more messages");
        loadOlderMessages();
      }
    };

    const scrollElement = chatContainerRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, isLoadingMore, loadOlderMessages]);
  
  // Save scroll position when starting to load older messages
  useEffect(() => {
    if (!chatContainerRef.current) return;
    
    const container = chatContainerRef.current;
    
    if (isLoadingMore) {
      // Save current scroll position before loading
      scrollPositionRef.current = container.scrollTop;
      previousScrollHeightRef.current = container.scrollHeight;
    }
  }, [isLoadingMore]);
  
  // Restore scroll position after older messages are loaded
  useLayoutEffect(() => {
    if (!chatContainerRef.current || isLoadingMore) return;
    if (scrollPositionRef.current === 0 || previousScrollHeightRef.current === 0) return;
    
    const container = chatContainerRef.current;
    
    // Use setTimeout to ensure DOM has been updated
    const timeoutId = setTimeout(() => {
      // Calculate the distance from bottom that was preserved
      const savedScrollBottom = previousScrollHeightRef.current;
      
      // Calculate new scroll position to maintain same distance from bottom
      const newScrollTop = container.scrollHeight - savedScrollBottom;
      
      container.scrollTop = newScrollTop;
      
      // Reset tracking values
      scrollPositionRef.current = 0;
      previousScrollHeightRef.current = 0;
    }, 50); // Increased delay for reliable DOM updates
    
    return () => clearTimeout(timeoutId);
  }, [isLoadingMore]); // Only depend on isLoadingMore, not chats.length

  // Initial scroll with content stability - ONLY for initial load
  useLayoutEffect(() => {
    if (!chatContainerRef.current || !isInitialized || !chats.length || isLoadingMore) return;
    
    // Only run scroll-to-bottom logic on initial load
    if (!isInitialLoadRef.current) return;

    const container = chatContainerRef.current;

    // Scroll immediately (data is ready, but images and videos are being loaded)
    container.scrollTop = container.scrollHeight;

    // Then wait for content to stabilize (images, videos)
    let resizeObserver: ResizeObserver;
    let timeoutId: NodeJS.Timeout;

    const waitForStability = () => {
      let lastHeight = container.scrollHeight;
      let stableCount = 0;
      let observerTriggered = false;
      const requiredStable = 2;
      
      resizeObserver = new ResizeObserver(() => {
        observerTriggered = true;
        const currentHeight = container.scrollHeight;
        
        if (currentHeight === lastHeight) {
          // If height is stable on first trigger, scroll immediately
          if (stableCount === 0) {
            container.scrollTop = container.scrollHeight;
            resizeObserver.disconnect();
            isInitialLoadRef.current = false; // Mark initial load as complete
            return;
          }
          
          stableCount++;
          if (stableCount >= requiredStable) {
            container.scrollTop = container.scrollHeight;
            resizeObserver.disconnect();
            isInitialLoadRef.current = false; // Mark initial load as complete
          }
        } else {
          stableCount = 0;
          lastHeight = currentHeight;
        }
      });

      resizeObserver.observe(container);
      
      // If ResizeObserver doesn't trigger within 500ms, assume content is already stable
      const checkTimeout = setTimeout(() => {
        if (!observerTriggered) {
          container.scrollTop = container.scrollHeight;
          resizeObserver?.disconnect();
          isInitialLoadRef.current = false; // Mark initial load as complete
        }
      }, 500);

      // Fallback timeout
      timeoutId = setTimeout(() => {
        container.scrollTop = container.scrollHeight;
        resizeObserver?.disconnect();
        isInitialLoadRef.current = false; // Mark initial load as complete
      }, 2000);
      
      return () => {
        clearTimeout(checkTimeout);
      };
    };

    const cleanup = waitForStability();

    return () => {
      resizeObserver?.disconnect();
      clearTimeout(timeoutId);
      cleanup?.();
    }
  }, [isInitialized, chats.length, isLoadingMore, selected?.pk]);

  // Reset initial load flag when room changes
  useEffect(() => {
    isInitialLoadRef.current = true;
  }, [selected?.pk]);

  useEffect(() => {
    if (selected) {
      if (selected.hasOwnProperty('participants') && selected.participants.length > 0) {
        selected.participants.map((i: any) => {
          if (i?.pk === user?.pk || selected?.host?.pk === user?.pk) {
            setIsJoined(true);
            return '';
          }
          // setIsJoined(false);
          // return '';
        });
      } else {
        if (selected?.host?.pk === user?.pk) {
          setIsJoined(true);
        } else {
          setIsJoined(false);
        }
      }
    }
  }, [selected]);
  
  // Memoized calculations to prevent unnecessary re-computations
  const { chatsPin, pinnedCount } = useMemo(() => {
    if (!chats || chats.length === 0) {
      return { chatsPin: [], pinnedCount: 0 };
    }
    
    const pinned = chats.filter((r: any) => r.pin);
    const count = pinned.length;
    
    return { chatsPin: pinned, pinnedCount: count };
  }, [chats]);

  const removeImage = (imageId: any) => {
    setImages(images?.filter((i:any) => i.id !== imageId));
  }

  const updateLastSeen = (id: any) => {
    // Check if i am the host of the room =>>>
    const ImHost = selected?.host?.pk === user?.pk;
    if (ImHost) {
      if (latestMsgId.current !== null && ImHost && selected?.host?.lastSeenMessage !== id) {
        selected.host.lastSeenMessage = id;
        const obj = {
          roomId: selected?.pk,
          host: selected?.host,
        }
        UpdateHostInRoom(obj);
      }
    } else {
      const meUser = selected?.participants?.findIndex((p: any) => p?.pk == user?.pk) ?? -1;
      if (latestMsgId.current !== null && meUser !== -1 && selected?.participants[meUser]?.lastSeenMessage !== id) {
        selected.participants[meUser].lastSeenMessage = id;
        const obj = {
          roomId: selected?.pk,
          user: selected?.participants,
        }
        updateParticipantsInRoom(obj);
      }
    }
    return true;
  }

  const closeDialog = () => {
    document.body.classList.remove('modal-open');
  };

  const closeModal = () => {
    setModalToggle(false);
  };

  const handleJoinRoom = () => {
    // Resume AudioContext inside tap handler — most trusted gesture on iOS
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContext) {
      try {
        const ctx = new AudioContext();
        ctx.resume();
        console.log('AudioContext resumed for iOS compatibility');
      } catch (error) {
        console.warn('Failed to resume AudioContext:', error);
      }
    }
    
    const request = {
      roomId: selected.pk,
      id: `${selected.pk}-${randomString()}`,
      user: user,
      time: getCurrentDate(),
    };

    sendJoinRequest(request).then((e) => {
      setToastValues({
        message: 'Join request has been sent',
        type: 'Success',
      });
      setModalToggle(true);
      setToast(true);
    }).catch((error) => {
      // Skip handling if user data is invalid (toast already shown by middleware)
      if (error.code === 'INVALID_USER_DATA') {
        return;
      }
      
      // Handle duplicate request error
      if (error.code === 'DUPLICATE_REQUEST') {
        setToastValues({
          message: 'You already have a pending request for this room. Please wait for the host to respond.',
          type: 'Warning',
        });
        setToast(true);
      } else {
        setToastValues({
          message: 'Something went wrong!',
          type: 'Error',
        });
        setToast(true);
      }
    });
  };

  const joinToggleMsg = () => (
    <div className={Styles.modalTopConatiner}>
      <div>
        <img
          src={socialPlayFavIcon}
          alt="chat info"
          className={Styles.joinImage}
        />
        <div className={`${Styles.modalBottomContent} text-center`}>
          We'll notify you when they accept. Until then browse some more room
          topics.
        </div>
        <div className="text-center">
        <Button
          label="BROWSE ROOM TOPICS"
          clickHandler={() => history.push("conversations-nearby")}
          cssClass={`${Styles.createRoomWidth}`}
        />
        </div>
      </div>
    </div>
  );

  const onMessageSent = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    // Ensure the newly sent message is in the DOM before scrolling
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  };

  return (
    <>
      <ChatHeader
        key={`header-${selected?.pk || 'no-room'}`}
        name={selected?.topic ?? 'Chat room'}
        chatInfo={chatInfo}
        selected={selected}
        isJoined={isJoined}
      />
      {!isJoined ? (
        <div className={Styles.joinRoom}>
          {selected?.pk && (
            <div className={Styles.joinButtonContainer}>
              <div
                onClick={handleJoinRoom}
                onKeyPress={handleJoinRoom}
                role="button"
                tabIndex={0}
                >
                <img
                  src={joinImage}
                  alt="chat info"
                  className={Styles['chatInfoImage-big']}
                  />
              </div>
            </div>
          )}
          <p>Join this room to start chatting</p>
          
          {/* Intro video below join button */}
          {selected?.intro_video_url && (
            <div className={Styles.introVideoContainer}>
              <VideoPlayer url={selected.intro_video_url} />
            </div>
          )}
          
        </div>
      ) : (
        <>
          <div ref={chatContainerRef} className={`customScrollBar ${Styles.chatContentWrapper}`}>
            {/* Loading spinner for older messages */}
            {isLoadingMore && <Loader spinner={Styles.topLoader} aligned />}
            
            {chats.length > 0 &&
              chats !== null &&
              chats.map((item: any, index: number) => {
                // if (!item) {
                //   return null;
                // }
                // pinnedCount is now calculated by useMemo - no need to increment here
                if (latestMsgId.current === null) {
                  latestMsgId.current = index;
                } else {
                  const currentTime = getMessageTime(item);
                  const latestTime = getMessageTime(chats[latestMsgId.current]);
                  if (currentTime > latestTime) {
                    latestMsgId.current = index;
                  }
                }
                return <MessageBox
                  selected={selected}
                  item={item}
                  replyItems={replyItems}
                  type=""
                  viewReplies={viewReplies}
                  key={item.id || `${item.timestamp || item.createdTime}-${item.sender?.pk || index}`}
                  actions={actions}
                  chatsPin={chatsPin}
                  pinCount = { pinnedCount }
                />
              })}
            <div ref={bottomRef} />
          </div>
          {images.length > 0 && (
            <div className={Styles.attachedMedia}>
              {images.map((i: any, index: number) => (
                <div
                  className={`position-relative ${Styles.attachedInnerMedia}`}
                  key={i.id || `image-${index}`}
                >
                  {i.type === "video" ? (
                    <video
                      width="320"
                      height="240"
                      className={Styles.attachedFile}
                      controls
                    >
                      <source src={i.path} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={i.path}
                      className={Styles.attachedFile}
                      alt="attached-file"
                    />
                  )}
                  <div className={Styles.closeIconWrapper}
                    onClick={() => removeImage(i.id)}>
                    <img src={closeImage} alt="close-icon" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {typing ? (
            <span
              className={`px-3 fontFifteen input-color font-weight-normal ${Styles.typingUser}`}
            >{`${user.name} is typing…`}</span>
          ) : (
            <div className="pb-2">&nbsp;</div>
          )}
          <ChatFooter
            images={(e: string) => setImages([...images, e])}
            clearImages={() => setImages([])}
            attachedImages={images}
            chats={chatContent}
            typing={setTyping}
            selected={selected}
            activateRoom={activateRoom}
            onMessageSent={onMessageSent}
          />
        </>
      )}

      {modalToggle && (
        <CustomModal
          modalTitle={
            "Your request to join this room has been sent to the host"
          }
          wrapperClass={Styles.publicProfileWrapper}
          heightClass={Styles.publicProfileBody}
          closeDialog={closeDialog}
          modalTitleClass={Styles.modalTitleClass}
          closeModal={closeModal}
          close
        >
          {joinToggleMsg()}
        </CustomModal>
      )}
      {toast && (
        <Toast
          message={toastValues.message}
          position="topRight"
          type={toastValues.type}
          handleCloseToast={setToast}
        />
      )}
    </>
  );
};
export default ChatContent;
